import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authApi, SessionState } from "../api/authApi";
import { User } from "@nicsmith/shared_types";
import i18n from "i18next";

// Combine SessionState with Redux state and add idToken
type SessionStateInRedux = SessionState & {
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null | undefined;
  lastAuthenticated: number | null; // Add timestamp for token expiry tracking
  /** Internal flag: true while impersonating another user. Prevents localStorage writes that would corrupt the admin's stored session. Not persisted. */
  _isImpersonating: boolean;
};

const initialState: SessionStateInRedux = {
  message: "",
  user: null,
  idToken: null,
  isAuthenticated: false,
  loading: true,
  error: null,
  lastAuthenticated: null,
  _isImpersonating: false,
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    /**
     * Manually log out the user and clear session state.
     */
    logoutUser: (state) => {
      state.message = "";
      state.user = null;
      state.idToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.lastAuthenticated = null;
      localStorage.removeItem("session");
    },

    /**
     * Set user session data, including idToken.
     */
    setSession: (
      state,
      action: PayloadAction<{ user: User; idToken: string }>
    ) => {
      const { user, idToken } = action.payload;
      state.message = "";
      state.user = user;
      state.idToken = idToken;
      state.isAuthenticated = true;
      state.error = null;
      state.lastAuthenticated = Date.now(); // Track authentication time

      // Skip localStorage write during impersonation to avoid corrupting the admin's stored session
      if (!state._isImpersonating) {
        localStorage.setItem(
          "session",
          JSON.stringify({
            user,
            idToken,
            lastAuthenticated: Date.now(),
          })
        );
      }
    },

    /**
     * Update just the ID token while preserving other session data
     */
    updateIdToken: (state, action: PayloadAction<string>) => {
      state.idToken = action.payload;
      state.lastAuthenticated = Date.now();

      // Skip localStorage write during impersonation to avoid corrupting the admin's stored session
      if (!state._isImpersonating && state.user) {
        localStorage.setItem(
          "session",
          JSON.stringify({
            user: state.user,
            idToken: action.payload,
            lastAuthenticated: Date.now(),
          })
        );
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    /**
     * Replace session user without persisting to localStorage.
     * Used during impersonation so localStorage keeps the admin's data.
     */
    setImpersonatedUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state._isImpersonating = true;
    },

    /**
     * Clear the impersonation flag so localStorage writes resume.
     * Called when exiting impersonation mode.
     */
    clearImpersonationFlag: (state) => {
      state._isImpersonating = false;
    },

    updateUser: (state, action: PayloadAction<Partial<NonNullable<User>>>) => {
      if (state.user) {
        Object.assign(state.user, action.payload);
        // Skip localStorage write during impersonation to avoid corrupting the admin's stored session
        if (!state._isImpersonating && state.idToken) {
          localStorage.setItem(
            "session",
            JSON.stringify({
              user: state.user,
              idToken: state.idToken,
              lastAuthenticated: state.lastAuthenticated,
            })
          );
        }
      }
    },

    clearSession: (state) => {
      state.message = "";
      state.user = null;
      state.idToken = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = null;
      state.lastAuthenticated = null;
      localStorage.removeItem("session");
    },

    /**
     * Rehydrate session from localStorage (with validation)
     */
    rehydrateSession: (state) => {
      try {
        const savedSession = localStorage.getItem("session");
        if (savedSession) {
          const { user, idToken, lastAuthenticated } = JSON.parse(savedSession);

          // Check if session is valid
          if (user && idToken) {
            // Optional: Check if token is not too old (e.g., 1 hour)
            const MAX_TOKEN_AGE = 60 * 60 * 1000; // 1 hour in milliseconds
            const tokenAge = lastAuthenticated
              ? Date.now() - lastAuthenticated
              : Infinity;

            if (tokenAge < MAX_TOKEN_AGE) {
              state.user = user;
              state.idToken = idToken;
              state.isAuthenticated = true;
              state.lastAuthenticated = lastAuthenticated || Date.now();
              state.error = null;
              // Keep loading = true so initAuthState can validate with Cognito first.
              // This prevents API calls from firing with a potentially stale token.
              return;
            } else {
              // Token too old, clear session
              localStorage.removeItem("session");
            }
          }
        }
      } catch {
        localStorage.removeItem("session");
      }
      // No valid session found - let initAuthState handle loading state
    },
  },

  /**
   * Handle API lifecycle actions from `authApi`.
   */
  extraReducers: (builder) => {
    // Handle authenticate (login/signup)
    builder
      .addMatcher(authApi.endpoints.authenticate.matchPending, (state) => {
        state.message = i18n.t("auth.authenticating", "Authenticating...");
        state.loading = true;
      })
      .addMatcher(
        authApi.endpoints.authenticate.matchFulfilled,
        (state, action) => {
          state.user = action.payload.user;
          // idToken comes from the request args, not the server response
          const resolvedToken = action.meta.arg.originalArgs.idToken;
          state.idToken = resolvedToken;
          state.message = action.payload.message;
          state.isAuthenticated = true;
          state.loading = false;
          state.error = null;
          state.lastAuthenticated = Date.now();

          localStorage.setItem(
            "session",
            JSON.stringify({
              user: action.payload.user,
              idToken: resolvedToken,
              lastAuthenticated: Date.now(),
            })
          );
        }
      )
      .addMatcher(
        authApi.endpoints.authenticate.matchRejected,
        (state, action) => {
          state.message = action.error?.message || i18n.t("auth.failedToAuthenticate", "Failed to authenticate");
          state.loading = false;
          state.error = action.error?.message;

          // Only clear user data if this is a new login attempt, not a token refresh
          if (!state.isAuthenticated) {
            state.user = null;
            state.idToken = null;
            state.isAuthenticated = false;
            state.lastAuthenticated = null;
            localStorage.removeItem("session");
          }
        }
      );

    // Handle logout
    builder
      .addMatcher(authApi.endpoints.logout.matchPending, (state) => {
        state.message = i18n.t("auth.loggingOut", "Logging out...");
        state.loading = true;
      })
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        state.message = i18n.t("auth.loggedOut", "Logged out");
        state.user = null;
        state.idToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
        state.lastAuthenticated = null;
        localStorage.removeItem("session");
      })
      .addMatcher(authApi.endpoints.logout.matchRejected, (state, action) => {
        state.message = action.error?.message || i18n.t("auth.failedToLogout", "Failed to log out");
        state.loading = false;
        state.error = action.error?.message;
      });
  },
});

// Export actions and reducer
export const {
  logoutUser,
  setSession,
  setLoading,
  clearSession,
  setError,
  updateIdToken,
  updateUser,
  setImpersonatedUser,
  clearImpersonationFlag,
  rehydrateSession,
} = sessionSlice.actions;

export default sessionSlice.reducer;
